/**
 * useSeo — composable reativo para gerenciar SEO de uma página.
 *
 * Cobre:
 *  - <title>
 *  - meta description / keywords / robots
 *  - canonical + hreflang (x-default, es, pt, en)
 *  - Open Graph (og:title, og:description, og:image, og:url, og:locale, og:type)
 *  - Twitter Card (summary_large_image)
 *  - JSON-LD (Product / FAQPage / Organization / BreadcrumbList — via prop structuredData)
 *
 * Ao desmontar a página, restaura o título original para que outras páginas
 * não herdem metadados residuais.
 *
 * Uso típico:
 *
 *   useSeo(() => ({
 *     lang: currentLang.value,         // 'es' | 'pt' | 'en'
 *     title: 'OligoScan agora é OligoCheck | Análise Mineral',
 *     description: '...',
 *     keywords: ['oligoscan', 'oligocheck', 'análise mineral'],
 *     canonical: 'https://portal.oligocheck.es/oligoscan',
 *     alternates: {
 *       es: 'https://portal.oligocheck.es/oligoscan',
 *       pt: 'https://portal.oligocheck.es/oligoscan?lang=pt',
 *       en: 'https://portal.oligocheck.es/oligoscan?lang=en'
 *     },
 *     image: 'https://.../og-image.png',
 *     structuredData: [ { '@context': 'https://schema.org', '@type': 'Product', ... } ]
 *   }))
 */

import { watchEffect, onBeforeUnmount } from 'vue'

const LOCALE_MAP = {
  es: 'es_ES',
  pt: 'pt_BR',
  en: 'en_US'
}

/** Garante a existência de uma tag <meta> com o atributo/valor dado (ex: name="description"). */
const setMeta = (attr, value, content, parent = document.head) => {
  if (!content) return
  let el = parent.querySelector(`meta[${attr}="${value}"]`)
  if (!el) {
    el = document.createElement('meta')
    el.setAttribute(attr, value)
    parent.appendChild(el)
  }
  el.setAttribute('content', content)
}

/** Garante a existência de uma tag <link rel="..." [hreflang="..."]>. */
const setLink = (rel, href, extraAttrs = {}) => {
  if (!href) return null
  const selectorParts = [`link[rel="${rel}"]`]
  Object.entries(extraAttrs).forEach(([k, v]) => selectorParts.push(`[${k}="${v}"]`))
  const selector = selectorParts.join('')
  let el = document.head.querySelector(selector)
  if (!el) {
    el = document.createElement('link')
    el.setAttribute('rel', rel)
    Object.entries(extraAttrs).forEach(([k, v]) => el.setAttribute(k, v))
    document.head.appendChild(el)
  }
  el.setAttribute('href', href)
  return el
}

/** Escreve (ou reescreve) todas as tags <script type="application/ld+json"> geradas por este composable. */
const SD_MARK = 'data-seo-jsonld'
const clearJsonLd = () => {
  document.head.querySelectorAll(`script[${SD_MARK}="true"]`).forEach(s => s.remove())
}
const writeJsonLd = (data) => {
  if (!data) return
  const arr = Array.isArray(data) ? data : [data]
  arr.forEach(obj => {
    const script = document.createElement('script')
    script.type = 'application/ld+json'
    script.setAttribute(SD_MARK, 'true')
    script.textContent = JSON.stringify(obj)
    document.head.appendChild(script)
  })
}

/** Remove todas as tags <link rel="alternate" hreflang="..."> geradas por este composable. */
const HREFLANG_MARK = 'data-seo-hreflang'
const clearHreflangs = () => {
  document.head
    .querySelectorAll(`link[rel="alternate"][${HREFLANG_MARK}="true"]`)
    .forEach(s => s.remove())
}
const writeHreflangs = (alternates = {}) => {
  Object.entries(alternates).forEach(([lang, href]) => {
    if (!href) return
    const link = document.createElement('link')
    link.setAttribute('rel', 'alternate')
    link.setAttribute('hreflang', lang)
    link.setAttribute('href', href)
    link.setAttribute(HREFLANG_MARK, 'true')
    document.head.appendChild(link)
  })
}

/**
 * useSeo
 * @param {() => object} metaFn — função que retorna os metadados; roda em watchEffect
 *                                (útil para reagir a troca de idioma).
 */
export const useSeo = (metaFn) => {
  const originalTitle = document.title

  const stop = watchEffect(() => {
    const meta = typeof metaFn === 'function' ? metaFn() : metaFn
    if (!meta) return

    const {
      lang,
      title,
      description,
      keywords,
      robots = 'index, follow, max-image-preview:large',
      canonical,
      alternates,
      image,
      imageAlt,
      siteName = 'OligoCheck',
      type = 'website',
      twitterSite,
      structuredData
    } = meta

    if (lang) {
      document.documentElement.setAttribute('lang', lang)
    }

    if (title) document.title = title

    setMeta('name', 'description', description)
    setMeta('itemprop', 'description', description)
    setMeta('name', 'keywords', Array.isArray(keywords) ? keywords.join(', ') : keywords)
    setMeta('name', 'robots', robots)
    setMeta('name', 'googlebot', robots)

    setMeta('property', 'og:title', title)
    setMeta('property', 'og:description', description)
    setMeta('property', 'og:url', canonical)
    setMeta('property', 'og:type', type)
    setMeta('property', 'og:site_name', siteName)
    setMeta('property', 'og:locale', LOCALE_MAP[lang] || LOCALE_MAP.es)
    setMeta('property', 'og:image', image)
    setMeta('property', 'og:image:alt', imageAlt || title)
    setMeta('itemprop', 'image', image)
    setMeta('name', 'image', image)

    setMeta('name', 'twitter:card', 'summary_large_image')
    setMeta('name', 'twitter:title', title)
    setMeta('name', 'twitter:description', description)
    setMeta('name', 'twitter:image', image)
    if (twitterSite) setMeta('name', 'twitter:site', twitterSite)

    setLink('canonical', canonical)

    clearHreflangs()
    if (alternates) {
      writeHreflangs(alternates)
      if (alternates['x-default'] || alternates.es) {
        writeHreflangs({ 'x-default': alternates['x-default'] || alternates.es })
      }
    }

    clearJsonLd()
    writeJsonLd(structuredData)
  })

  onBeforeUnmount(() => {
    stop()
    clearJsonLd()
    clearHreflangs()
    document.title = originalTitle
  })
}

export default useSeo
